/**
 * Голосовой ввод — один на весь кабинет (правила — `@/lib/voice`): запись в браузере
 * (`MediaRecorder`, Opus 32 кбит/с), таймер, уровень микрофона, предел 4 минуты.
 *
 * Два способа отдать запись:
 * - `useVoice({ onDone })` — сразу после «Стоп» запись уходит в `onDone` (загрузка и всё, что с ней
 *   связано); пока идёт — «Отправляю…», ушла — запись сброшена, в поле ничего не вставляем:
 *   расшифровка придёт фоном. Не ушла — запись в памяти, «Отправить ещё раз».
 * - `useVoice()` — запись лежит до отправки формы (`v.blob`), её можно прослушать и перезаписать.
 *
 * Жизненный цикл: у каждого запуска свой номер; ответ на запрос микрофона, пришедший после ухода
 * со страницы или после нового запуска, сразу гасит полученный поток. Остановка и уход освобождают
 * всё: поток, recorder, AudioContext, ссылку на запись.
 */
import { LoaderCircle, Mic, RotateCcw, Send, Square, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { MAX_AUDIO } from "@/lib/voice";

const LIMIT = 4 * 60;
const TYPES = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"];

export type Voice = {
  state: "idle" | "requesting" | "recording" | "ready" | "sending" | "error";
  seconds: number;
  blob: Blob | null;
  url: string | null;
  error: string;
  level: number;
  /** Сколько записей ушло через `onDone` с открытия. */
  sent: number;
};
const IDLE: Voice = { state: "idle", seconds: 0, blob: null, url: null, error: "", level: 0, sent: 0 };

export function useVoice(opts: { onDone?: (blob: Blob, seconds: number) => Promise<unknown> } = {}) {
  const [v, setV] = useState<Voice>(IDLE);
  const rec = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<number | null>(null);
  const meter = useRef<{ ctx: AudioContext; raf: number } | null>(null);
  const run = useRef(0);
  const mounted = useRef(true);
  const url = useRef<string | null>(null);
  // Свежий `onDone` без пересоздания записи: он видит отметки и текст формы на момент «Стоп».
  const done = useRef(opts.onDone);
  done.current = opts.onDone;
  const auto = !!opts.onDone;

  const release = useCallback(() => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
    if (meter.current) {
      cancelAnimationFrame(meter.current.raf);
      void meter.current.ctx.close().catch(() => undefined);
      meter.current = null;
    }
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }, []);

  const dropUrl = useCallback(() => {
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null;
  }, []);

  /** Отдать запись в `onDone`; удача — запись сброшена, неудача — остаётся с ошибкой. */
  const deliver = useCallback(
    async (blob: Blob, seconds: number) => {
      const my = run.current;
      setV((x) => ({ ...x, state: "sending", error: "" }));
      try {
        await done.current?.(blob, seconds);
        if (my !== run.current || !mounted.current) return;
        dropUrl();
        setV((x) => ({ ...IDLE, sent: x.sent + 1 }));
      } catch (e) {
        if (my !== run.current || !mounted.current) return;
        setV((x) => ({ ...x, state: "ready", error: e instanceof Error ? e.message : "Не отправилось" }));
      }
    },
    [dropUrl],
  );

  const stop = useCallback(() => {
    if (rec.current && rec.current.state !== "inactive") rec.current.stop();
  }, []);

  const start = useCallback(async () => {
    if (rec.current && rec.current.state !== "inactive") return; // уже пишем
    const my = ++run.current;
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setV((x) => ({ ...IDLE, sent: x.sent, state: "error", error: "Браузер не умеет записывать звук — напишите текстом" }));
      return;
    }
    dropUrl();
    setV((x) => ({ ...IDLE, sent: x.sent, state: "requesting" }));
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      if (my !== run.current || !mounted.current) return;
      const name = e instanceof DOMException ? e.name : "";
      const error =
        name === "NotAllowedError"
          ? "Нет разрешения на микрофон — разрешите его у адресной строки и нажмите ещё раз"
          : name === "NotFoundError"
            ? "Микрофон не найден — подключите его или напишите текстом"
            : "Микрофон занят или недоступен — напишите текстом";
      setV((x) => ({ ...IDLE, sent: x.sent, state: "error", error }));
      return;
    }
    if (my !== run.current || !mounted.current) {
      media.getTracks().forEach((t) => t.stop()); // разрешение пришло, когда уже не нужно
      return;
    }
    stream.current = media;
    const mimeType = TYPES.find((t) => MediaRecorder.isTypeSupported(t));
    let r: MediaRecorder;
    try {
      r = new MediaRecorder(media, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 32000 });
    } catch {
      release();
      setV((x) => ({ ...IDLE, sent: x.sent, state: "error", error: "Не получилось начать запись — напишите текстом" }));
      return;
    }
    const chunks: Blob[] = [];
    const t0 = Date.now();
    r.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    r.onstop = () => {
      release();
      rec.current = null;
      if (my !== run.current || !mounted.current) return; // ушли со страницы — запись не нужна
      // Последний кусок приходит перед `stop` — запись целиком только здесь.
      const blob = new Blob(chunks, { type: r.mimeType || mimeType || "audio/webm" });
      const seconds = Math.round((Date.now() - t0) / 1000);
      const big = blob.size > MAX_AUDIO;
      dropUrl();
      url.current = URL.createObjectURL(blob);
      setV((x) => ({
        ...x,
        state: "ready",
        blob,
        url: url.current,
        seconds,
        level: 0,
        error: big ? "Запись больше 1,9 МБ — не загрузится; запишите короче" : "",
      }));
      if (done.current && !big) void deliver(blob, seconds);
    };
    rec.current = r;
    try {
      r.start(1000);
    } catch {
      rec.current = null;
      release();
      setV((x) => ({ ...IDLE, sent: x.sent, state: "error", error: "Не получилось начать запись — напишите текстом" }));
      return;
    }
    setV((x) => ({ ...IDLE, sent: x.sent, state: "recording" }));
    timer.current = window.setInterval(() => {
      const s = Math.floor((Date.now() - t0) / 1000);
      setV((x) => (x.state === "recording" ? { ...x, seconds: s } : x));
      if (s >= LIMIT) stop();
    }, 250);
    let ctx: AudioContext | null = null;
    try {
      ctx = new AudioContext();
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(media).connect(an);
      const buf = new Uint8Array(an.fftSize);
      const tick = () => {
        an.getByteTimeDomainData(buf);
        let peak = 0;
        for (const b of buf) peak = Math.max(peak, Math.abs(b - 128));
        setV((x) => (x.state === "recording" ? { ...x, level: Math.min(1, peak / 64) } : x));
        if (meter.current) meter.current.raf = requestAnimationFrame(tick);
      };
      meter.current = { ctx, raf: requestAnimationFrame(tick) };
    } catch {
      // Уровень — украшение, без него запись идёт; созданный контекст не оставляем.
      void ctx?.close().catch(() => undefined);
    }
  }, [deliver, dropUrl, release, stop]);

  /** Повторить отправку записи, которая не ушла. */
  const retry = useCallback(() => {
    if (v.blob && v.blob.size <= MAX_AUDIO) void deliver(v.blob, v.seconds);
  }, [deliver, v.blob, v.seconds]);

  const reset = useCallback(() => {
    run.current++;
    stop();
    release();
    dropUrl();
    setV((x) => ({ ...IDLE, sent: x.sent }));
  }, [dropUrl, release, stop]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      run.current++;
      if (rec.current && rec.current.state !== "inactive") rec.current.stop();
      release();
      dropUrl();
    };
  }, [dropUrl, release]);

  return { v, start, stop, reset, retry, auto };
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
/** Идёт запись, ждём разрешение или запись уходит — отправлять форму нельзя. */
export const voiceBusy = (v: Voice) => v.state === "recording" || v.state === "requesting" || v.state === "sending";

/**
 * Кнопка микрофона и состояние записи в одну строку. `sentText` — что сказать после ухода записи
 * в режиме `onDone` (по умолчанию «голос ушёл, текст появится сам»).
 */
export function VoiceControl({
  voice,
  disabled,
  compact,
  sentText,
  onDrop,
}: {
  voice: ReturnType<typeof useVoice>;
  disabled?: boolean;
  compact?: boolean;
  sentText?: string;
  /** Загруженная запись ещё ждёт формы — крестик «не прикладывать» (новую до этого не начать). */
  onDrop?: (() => void) | undefined;
}) {
  const { v, start, stop, reset, retry, auto } = voice;
  if (v.state === "requesting") {
    return <span className="text-[13px] text-[var(--tg-sec,var(--fg-muted))]">Разрешите микрофон…</span>;
  }
  if (v.state === "recording") {
    return (
      <div className="flex min-w-0 items-center gap-2" data-annot="идёт запись голоса">
        <span className="size-2.5 flex-none animate-pulse rounded-full bg-[#e53935]" />
        <span className="tabular-nums text-[13px]">{mmss(v.seconds)}</span>
        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-black/10">
          <span className="block h-full bg-[var(--tg-primary,var(--accent))] transition-[width] duration-75" style={{ width: `${Math.round(v.level * 100)}%` }} />
        </span>
        {v.seconds >= LIMIT - 30 && <span className="text-[12px] text-[#e53935]">осталось {LIMIT - v.seconds} с</span>}
        <button
          type="button"
          onClick={stop}
          className="flex min-h-9 items-center gap-1 rounded-full bg-[#e53935] px-3 py-1 text-[13px] font-medium text-white hover:opacity-90"
          title={auto ? "Остановить — запись уйдёт сразу, расшифровка придёт сама" : "Остановить запись"}
        >
          <Square size={12} fill="currentColor" /> {auto ? "Стоп и отправить" : "Стоп"}
        </button>
      </div>
    );
  }
  if (v.state === "sending") {
    return (
      <span className="flex items-center gap-1.5 text-[13px] text-[var(--tg-sec,var(--fg-muted))]" data-annot="голос уходит">
        <LoaderCircle size={15} className="animate-spin motion-reduce:animate-none" /> Отправляю голос…
      </span>
    );
  }
  if (v.state === "ready" && v.url) {
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-1.5" data-annot="записанный голос">
        <audio src={v.url} controls className="h-8 max-w-[240px]" />
        {auto && v.blob && v.blob.size <= MAX_AUDIO && (
          <button
            type="button"
            onClick={retry}
            disabled={disabled}
            title="Отправить эту запись ещё раз"
            className="flex min-h-9 items-center gap-1 rounded-full bg-[var(--tg-primary,var(--accent))] px-3 py-1 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Send size={13} /> Отправить ещё раз
          </button>
        )}
        <button type="button" onClick={() => void start()} disabled={disabled} title="Перезаписать" className="grid size-9 place-items-center rounded-full text-[var(--tg-sec,var(--fg-muted))] hover:bg-[var(--tg-hover,var(--bg-hover))]">
          <RotateCcw size={16} />
        </button>
        <button type="button" onClick={reset} disabled={disabled} title="Убрать запись" className="grid size-9 place-items-center rounded-full text-[var(--tg-sec,var(--fg-muted))] hover:bg-[var(--tg-hover,var(--bg-hover))]">
          <Trash2 size={16} />
        </button>
        {v.error && <span className="text-[12px] text-[#e53935]">{v.error}</span>}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        onClick={() => void start()}
        disabled={disabled}
        title={auto ? "Надиктовать голосом (до 4 минут): после «Стоп» запись уйдёт сама, текст расшифруется фоном" : "Надиктовать голосом (до 4 минут); расшифровка придёт сама"}
        className="flex min-h-9 flex-none items-center gap-1.5 rounded-full bg-[var(--tg-surface,var(--bg-subtle))] px-3 py-1 text-[13px] font-medium text-[var(--tg-primary,var(--accent))] shadow-[var(--tg-shadow,none)] hover:bg-[var(--tg-hover,var(--bg-hover))] disabled:opacity-50"
        data-annot="надиктовать голосом"
      >
        <Mic size={15} /> {compact ? "" : "Голосом"}
      </button>
      {v.state === "error" && <span className="text-[12px] text-[#e53935]">{v.error}</span>}
      {v.state === "idle" && auto && v.sent > 0 && (
        <span className="text-[12px] text-[var(--tg-sec,var(--fg-muted))]">{sentText ?? "Голос ушёл — текст появится сам"}</span>
      )}
      {onDrop && v.state === "idle" && (
        <button
          type="button"
          onClick={onDrop}
          title="Не прикладывать загруженный голос — можно записать заново"
          className="grid size-7 flex-none place-items-center rounded-full text-[var(--tg-sec,var(--fg-muted))] hover:bg-[var(--tg-hover,var(--bg-hover))]"
        >
          <X size={15} />
        </button>
      )}
    </div>
  );
}
